import numeral from "numeral";
import { chartConfig, MULTI_TILE_DEFAULTS, SINGLE_CHART_DEFAULTS, YAxisWidthConfig } from "../chartConfig";
import { IHierarchyDef, IHierarchySplit } from "src/pages/Insights/models/interfaces";
import { useMemo } from "react";

export type AxisRoundingMethod = "nice" | "nearest";

export interface IAxisRangeOptions {
	method?: AxisRoundingMethod;
	headroom?: number;
	minAxis?: number;
	tickCount?: number;
}

export interface IAxisRangeResult {
	axisMax: number;
	ticks: number[];
}

function niceCeil(value: number): number {
	if (value <= 0) return 1;
	const exp = Math.floor(Math.log10(value));
	const magnitude = Math.pow(10, exp);
	const normalized = value / magnitude;

	if (normalized <= 1) return magnitude;
	if (normalized <= 2) return 2 * magnitude;
	if (normalized <= 5) return 5 * magnitude;
	return 10 * magnitude;
}

function ceilToNearest(value: number): number {
	if (value <= 0) return 1;
	const exp = Math.floor(Math.log10(value));
	const magnitude = Math.pow(10, exp);
	const normalized = value / magnitude;

	let step: number;
	if (normalized <= 1) step = 0.1;
	else if (normalized <= 2) step = 0.2;
	else if (normalized <= 5) step = 0.5;
	else step = 1;

	const absStep = step * magnitude;
	return Math.ceil(value / absStep) * absStep;
}

export function computeStackedAxisRange(stackTotal: number, { method = "nearest", headroom = 0.05, minAxis = 0, tickCount = 5 }: IAxisRangeOptions = {}): IAxisRangeResult {
	if (!isFinite(stackTotal) || stackTotal <= 0) {
		const fallbackMax = Math.max(minAxis, 1);
		return {
			axisMax: fallbackMax,
			ticks: generateTicks(0, fallbackMax, tickCount),
		};
	}

	const target = stackTotal * (1 + headroom);
	const rounded = method === "nice" ? niceCeil(target) : ceilToNearest(target);
	const axisMax = parseFloat(Math.max(rounded, target, minAxis).toPrecision(12));

	return {
		axisMax,
		ticks: generateTicks(0, axisMax, tickCount),
	};
}

function generateTicks(min: number, max: number, count: number): number[] {
	const step = (max - min) / (count - 1);
	return Array.from({ length: count }, (_, i) => parseFloat((min + i * step).toPrecision(12)));
}

export const getMaxStackTotal = (columns: Array<{ seriesData: Array<{ value: number }> }>): number => {
	if (!columns.length) return 0;
	return Math.max(...columns.map((col) => col.seriesData.reduce((sum, s) => sum + (typeof s.value === "number" && isFinite(s.value) ? s.value : 0), 0)));
};

/**
 * Calculate synchronized Y-axis domain for all chart types.
 *
 * For STACKED charts: pass `stackTotal` (max column-wise sum across categories).
 * For CLUSTERED / LINE charts: pass `allValues` (all individual data points).
 *
 * BE sends percentage values as decimals (0.8 = 80%).
 * Multiply by 100 for domain calculation if labelFormat contains "%".
 */
export const calculateYAxisDomain = (
	allValues: number[],
	labelFormat: string,
	stackTotal?: number,
	axisRangeOptions?: IAxisRangeOptions,
): { min: number; max: number; interval: number } => {
	const config = chartConfig.getDomainConfig();
	const isPercentage = labelFormat.includes("%");

	if (stackTotal !== undefined) {
		const processedTotal = isPercentage ? stackTotal * 100 : stackTotal;

		if (isPercentage && processedTotal <= config.percentage.maxThreshold) {
			return { min: 0, max: 100, interval: 25 };
		}

		const { axisMax, ticks } = computeStackedAxisRange(processedTotal, {
			method: "nearest",
			headroom: 0.05,
			tickCount: 5,
			...axisRangeOptions,
		});

		return {
			min: 0,
			max: axisMax,
			interval: ticks.length >= 2 ? ticks[1] - ticks[0] : axisMax / 4,
		};
	}

	if (allValues.length === 0) {
		return { min: 0, max: 100, interval: 25 };
	}

	const processedValues = isPercentage ? allValues.map((v) => v * 100) : allValues;
	const maxValue = Math.max(...processedValues);
	const minValue = Math.min(...processedValues);

	if (isPercentage) {
		const { maxThreshold, bufferMultiplier, roundingDivisor } = config.percentage;
		const { intervalDivisor } = config.numeric;

		let domainMax: number;
		if (maxValue <= 0) {
			domainMax = 0;
		} else if (maxValue <= maxThreshold) {
			domainMax = 100;
		} else {
			const maxWithBuffer = maxValue * bufferMultiplier;
			domainMax = Math.ceil(maxWithBuffer / roundingDivisor) * roundingDivisor;
		}

		const domainMin =
			minValue >= 0
				? 0
				: Math.floor((minValue * bufferMultiplier) / roundingDivisor) * roundingDivisor;

		return {
			min: domainMin,
			max: domainMax,
			interval: (domainMax - domainMin) / intervalDivisor,
		};
	}

	if (maxValue >= 90 && maxValue <= config.percentage.maxThreshold && minValue >= 0) {
		return { min: 0, max: 100, interval: 25 };
	}

	const { bufferMultiplier, roundingDivisor, minRoundValue, intervalDivisor } = config.numeric;
	const maxWithBuffer = maxValue * bufferMultiplier;

	let roundedMax: number;
	if (maxValue <= 0) {
		roundedMax = 0;
	} else {
		const tentative = Math.ceil(maxWithBuffer / roundingDivisor) * roundingDivisor;
		roundedMax = tentative < minRoundValue ? Math.ceil(maxWithBuffer) : tentative;
	}

	let roundedMin: number;
	if (minValue >= 0) {
		roundedMin = 0;
	} else {
		const minWithBuffer = minValue * bufferMultiplier;
		const tentative = Math.floor(minWithBuffer / roundingDivisor) * roundingDivisor;
		roundedMin = Math.abs(tentative) < minRoundValue ? Math.floor(minWithBuffer) : tentative;
	}

	return {
		min: roundedMin,
		max: roundedMax,
		interval: (roundedMax - roundedMin) / intervalDivisor,
	};
};

/**
 * Format number according to the label format.
 *
 * - For data labels/tooltips: BE sends percentage values as decimals (0.8 = 80%), multiply by 100
 * - For Y-axis labels: Values are already in percentage scale (100 = 100%), do NOT multiply
 * - For very large numbers (> 1000): Display in K, M, B format
 */
export const formatDataLabel = (value: number, format: string, isYAxisLabel: boolean = false, needDecimal: boolean = true): string => {
	if (!format) return String(value);

	const cleanFormat = format.replace(/\*\*/g, "");

	try {
		if (cleanFormat.includes("%")) {
			const percentValue = isYAxisLabel ? value : value * 100;

			if (!needDecimal) {
				return numeral(percentValue).format("0") + "%";
			}

			const numberPart = cleanFormat.replace(/%/g, "");
			if (numberPart.includes(".")) {
				return numeral(percentValue).format("0.0") + "%";
			}
			return numeral(percentValue).format("0") + "%";
		}

		if (!needDecimal) {
			if (Math.abs(value) > 1000) {
				return numeral(value).format("0a");
			}
			return numeral(value).format("0");
		}

		if (cleanFormat.includes(".")) {
			const decimalPlaces = cleanFormat.split(".")[1]?.length || 1;
			if (Math.abs(value) > 1000) {
				return numeral(value).format("0.0a");
			}
			return numeral(value).format("0." + "0".repeat(decimalPlaces));
		}

		if (Math.abs(value) > 1000) {
			return numeral(value).format("0a");
		}
		return numeral(value).format("0");
	} catch (error) {
		console.error("Error formatting label:", error);
		return String(value);
	}
};

export const extractAllValues = (columns: any[]): number[] => {
	const values: number[] = [];

	columns.forEach((col) => {
		if (col.seriesData && Array.isArray(col.seriesData)) {
			col.seriesData.forEach((series: any) => {
				if (typeof series.value === "number" && isFinite(series.value)) {
					values.push(series.value);
				}
			});
		}
	});

	return values;
};

export const extractStackedValues = (columns: any[]): number[] => {
	const values: number[] = [];

	columns.forEach((col) => {
		if (col.seriesData && Array.isArray(col.seriesData)) {
			const stackSum = col.seriesData.reduce((sum: number, series: any) => {
				return sum + (typeof series.value === "number" && isFinite(series.value) ? series.value : 0);
			}, 0);
			values.push(stackSum);
		}
	});

	return values;
};

export interface HeaderTextStyle {
	className: string;
	fontSize: number;
	lineHeight: number;
}

export const getHeaderTextStyle = (levelIndex: number, isLastLevel: boolean, scaleFactor: number = 1): HeaderTextStyle => {
	const colorsConfig = chartConfig.getColorsConfig();

	const baseSize = 11;
	const fontSize = Math.max(8, Math.round(baseSize * scaleFactor));

	const lineHeight = fontSize + 4;
	const fontWeight = "font-normal";

	let colorClass: string;
	switch (levelIndex) {
		case 0:
			colorClass = `text-[${colorsConfig.text.quaternary}]`;
			break;
		case 1:
		case 2:
			colorClass = `text-[${colorsConfig.text.tertiary}]`;
			break;
		default:
			colorClass = `text-[${colorsConfig.text.secondary}]`;
	}

	return {
		className: `${colorClass} ${fontWeight}`,
		fontSize,
		lineHeight,
	};
};

export const getHeaderTextStyleWithBorder = (
	levelIndex: number,
	isLastLevel: boolean,
): {
	className: string;
	borderStyle: React.CSSProperties;
} => {
	const colorsConfig = chartConfig.getColorsConfig();
	const baseFontSize = isLastLevel ? "text-[11px]" : "text-[12px]";
	const fontWeight = "font-normal";

	let className: string;
	let borderStyle: React.CSSProperties = {};

	switch (levelIndex) {
		case 0:
			className = `text-[${colorsConfig.text.quaternary}] ${fontWeight} ${baseFontSize}`;
			borderStyle = {
				borderTopWidth: 1,
				borderTopStyle: "solid",
				borderTopColor: colorsConfig.borders.solid,
				borderBottomWidth: 1,
				borderBottomStyle: "solid",
				borderBottomColor: colorsConfig.borders.solid,
			};
			break;
		case 1:
			className = `text-[${colorsConfig.text.secondary}] ${fontWeight} ${baseFontSize}`;
			borderStyle = {
				borderBottomWidth: 1,
				borderBottomStyle: "solid",
				borderBottomColor: colorsConfig.borders.solid,
			};
			break;
		case 2:
			className = `text-[${colorsConfig.text.tertiary}] ${fontWeight} ${baseFontSize}`;
			break;
		default:
			className = `text-[${colorsConfig.text.secondary}] ${fontWeight} ${baseFontSize}`;
	}

	return { className, borderStyle };
};

const DEFAULT_MAX_DEPTH = 8;

interface CheckOptions {
	threshold?: number;
	maxDepth?: number;
	onMaxDepthReached?: () => void;
}

export function hasLowNSize(data: unknown, options: CheckOptions = {}): boolean {
	if (!data) return false;
	const { threshold = 30, maxDepth = DEFAULT_MAX_DEPTH, onMaxDepthReached } = options;
	const visited = new WeakSet<object>();
	const queue: Array<[unknown, number]> = [[data, 0]];

	while (queue.length > 0) {
		const [node, depth] = queue.shift()!;
		if (depth > maxDepth) {
			onMaxDepthReached?.();
			continue;
		}
		if (!node) continue;

		if (typeof node === "object") {
			if (visited.has(node as object)) continue;
			visited.add(node as object);
		}

		if (Array.isArray(node)) {
			if (node.some((item) => item && typeof item === "object" && "n_size" in item && typeof item.n_size === "number" && item.n_size < threshold)) {
				return true;
			}
			node.forEach((item) => queue.push([item, depth + 1]));
			continue;
		}

		if (typeof node === "object") {
			const obj = node as Record<string, unknown>;

			if (Array.isArray(obj.series) && obj.series.some((item: any) => typeof item?.n_size === "number" && item.n_size < threshold)) {
				return true;
			}

			Object.values(obj).forEach((value) => queue.push([value, depth + 1]));
		}
	}

	return false;
}

export const splitHierarchy = (hierarchy: IHierarchyDef[]): IHierarchySplit => {
	if (!hierarchy || hierarchy.length === 0) {
		return { columnHierarchies: [], rowHierarchy: null };
	}

	const rowHierarchy = hierarchy.find((h) => h.orientation === "row") || null;
	const columnHierarchies = hierarchy.filter((h) => h.orientation !== "row");

	return { columnHierarchies, rowHierarchy };
};

type FlatColumn = {
	path: string[];
	key: string;
	wavesData: Record<string, any[]>;
	n_size?: { min: number; max: number } | "No Data";
};

type CellType = "header" | "nsize" | "attrLabel" | "waveLabel" | "data";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	waveIndex?: number;
	totalWaves: number;
	totalRows: number;
	totalCols: number;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

export const getCellBorders = (cellType: CellType, position: ICellPosition, columns: FlatColumn[], colorsConfig: any, showWaveColumn: boolean): IBorderConfig => {
	const solidBorder = `1px solid ${colorsConfig.borders.solid}`;
	const dashedBorder = `1px dashed ${colorsConfig.borders.dashed}`;
	const lightBorder = `1px solid ${colorsConfig.borders.lightGray}`;
	const transparentBorder = `1px solid transparent`;

	const { rowIndex, colIndex, waveIndex = 0, totalWaves } = position;

	const fixedColCount = showWaveColumn ? 2 : 1;
	const dataColIndex = colIndex - fixedColCount;

	const isL0Break = (dataIdx: number): boolean => {
		if (dataIdx <= 0 || dataIdx >= columns.length) return false;
		return columns[dataIdx].path[0] !== columns[dataIdx - 1].path[0];
	};

	const isSubLevelBreak = (dataIdx: number): boolean => {
		if (dataIdx <= 0 || dataIdx >= columns.length) return false;
		const col = columns[dataIdx];
		const prevCol = columns[dataIdx - 1];
		if (col.path[0] !== prevCol.path[0]) return false;
		for (let l = 1; l < col.path.length - 1; l++) {
			if (col.path[l] !== prevCol.path[l]) return true;
		}
		return false;
	};

	const isLastWave = waveIndex === totalWaves - 1;
	const isFirstDataCol = dataColIndex === 0;

	switch (cellType) {
		case "header": {
			const borders: IBorderConfig = {
				borderTop: solidBorder,
				borderBottom: solidBorder,
			};

			if (isFirstDataCol) {
				borders.borderLeft = solidBorder;
			} else if (isL0Break(dataColIndex)) {
				borders.borderLeft = solidBorder;
			} else if (isSubLevelBreak(dataColIndex)) {
				borders.borderLeft = dashedBorder;
			} else {
				borders.borderLeft = transparentBorder;
			}

			return borders;
		}

		case "nsize": {
			const borders: IBorderConfig = {
				borderTop: solidBorder,
				borderBottom: solidBorder,
			};

			if (isFirstDataCol) {
				borders.borderLeft = solidBorder;
			} else if (isL0Break(dataColIndex)) {
				borders.borderLeft = solidBorder;
			} else if (isSubLevelBreak(dataColIndex)) {
				borders.borderLeft = dashedBorder;
			} else {
				borders.borderLeft = transparentBorder;
			}

			return borders;
		}

		case "attrLabel":
			return {
				borderRight: lightBorder,
				borderBottom: solidBorder,
			};

		case "waveLabel":
			return {
				borderRight: solidBorder,
				borderBottom: isLastWave ? undefined : lightBorder,
			};

		case "data": {
			const borders: IBorderConfig = {};

			if (isFirstDataCol) {
				borders.borderLeft = solidBorder;
			} else if (isL0Break(dataColIndex)) {
				borders.borderLeft = solidBorder;
			} else {
				borders.borderLeft = transparentBorder;
			}

			if (!isLastWave) {
				borders.borderBottom = lightBorder;
			}

			return borders;
		}

		default:
			return {};
	}
};

export const normalizeStatMarker = (marker?: string | string[]): string => {
	if (!marker) return "";
	if (Array.isArray(marker)) return marker.filter(Boolean).join(", ");
	return marker;
};

export const useResponsiveYAxisWidth = (attributes: string[], customWidth?: number, customMin?: number, customMax?: number, scaleFactor?: number) => {
	return useMemo(() => {
		const config = chartConfig.getHorizontalClusteredConfig();
		const minWidth = customMin ?? config.yAxisLabel.minWidth;
		const maxWidth = customMax ?? config.yAxisLabel.maxWidth;

		if (customWidth) {
			return Math.min(Math.max(customWidth, minWidth), maxWidth);
		}

		const effectiveScale = scaleFactor ?? getResponsiveScale();
		return calculateResponsiveYAxisWidth(attributes, effectiveScale, minWidth, maxWidth);
	}, [attributes, scaleFactor, customWidth, customMin, customMax]);
};

export const getResponsiveScale = (): number => {
	if (typeof window === "undefined") return 1;

	const config = chartConfig.getResponsiveConfig();
	const width = window.innerWidth;

	if (width < config.breakpoints.mobile) return config.scales.mobile;
	if (width < config.breakpoints.tablet) return config.scales.tablet;
	return config.scales.desktop;
};

export const calculateResponsiveYAxisWidth = (attributes: string[], scaleOverride?: number, minWidthOverride?: number, maxWidthOverride?: number): number => {
	const config = chartConfig.getHorizontalClusteredConfig();
	const typographyConfig = chartConfig.getTypographyConfig();
	const scale = scaleOverride ?? getResponsiveScale();

	const minWidth = minWidthOverride ?? config.yAxisLabel.minWidth;
	const maxWidth = maxWidthOverride ?? config.yAxisLabel.maxWidth;

	if (!attributes.length) {
		return Math.max(minWidth, Math.floor(maxWidth * scale));
	}

	const longestAttr = attributes.reduce((a, b) => ((a?.length ?? 0) >= (b?.length ?? 0) ? a : b), "");
	const fontSize = Math.max(10, Math.round(11 * scale));
	const measuredWidth = measureTextWidth(longestAttr, `400 ${fontSize}px sans-serif`);

	const textWidth = measuredWidth > 0 ? measuredWidth : Math.max(...attributes.map((attr) => attr?.length ?? 0)) * typographyConfig.charWidth;

	const isCompactMode = maxWidth <= 120;
	const BASE_PADDING = isCompactMode ? 12 : 32;

	const calculatedWidth = Math.ceil(textWidth) + BASE_PADDING;

	return Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
};

/* -------------------- Text Measurement -------------------- */

const measureCache = new Map<string, number>();

export function measureTextWidth(text: string, font: string): number {
	if (typeof document === "undefined") return 0;
	const key = `${font}|${text}`;
	const cached = measureCache.get(key);
	if (cached !== undefined) return cached;

	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (!ctx) return 0;
	ctx.font = font;
	const width = Math.ceil(ctx.measureText(text).width);
	measureCache.set(key, width);

	if (measureCache.size > 500) {
		const firstKey = measureCache.keys().next().value;
		if (firstKey) measureCache.delete(firstKey);
	}

	return width;
}

function estimateTextWidth(text: string, charWidth: number): number {
	return text.length * charWidth;
}

/* -------------------- Y-Axis Width (Hybrid) -------------------- */

export interface ComputeYAxisWidthParams {
	attributes: string[];
	containerWidth: number;
	scaleFactor?: number;
	config?: Partial<YAxisWidthConfig>;
	isMultiTile?: boolean;
}

export interface YAxisWidthResult {
	width: number;
	willTruncate: boolean;
	idealWidth: number;
}

export function computeYAxisWidth({ attributes, containerWidth, scaleFactor = 1, config: configOverride, isMultiTile = false }: ComputeYAxisWidthParams): YAxisWidthResult {
	const defaults = isMultiTile ? MULTI_TILE_DEFAULTS : SINGLE_CHART_DEFAULTS;
	const cfg: YAxisWidthConfig = { ...defaults, ...configOverride };

	const typoCfg = chartConfig.getTypographyConfig();
	const fontSize = Math.max(8, Math.round(11 * scaleFactor));
	const font = `400 ${fontSize}px "Open Sans", sans-serif`;

	let maxTextWidth = 0;
	if (attributes.length > 0) {
		const sorted = [...attributes].sort((a, b) => b.length - a.length);
		const toMeasure = sorted.slice(0, 5);

		for (const attr of toMeasure) {
			const measured = measureTextWidth(attr, font);
			const width = measured > 0 ? measured : estimateTextWidth(attr, typoCfg.charWidth);
			if (width > maxTextWidth) maxTextWidth = width;
		}
	}

	const rawIdealWidth = maxTextWidth + cfg.paddingPx;
	const idealWidth = Math.ceil(rawIdealWidth * (cfg.expansionFactor ?? 1));

	const containerCeiling = containerWidth > 0 ? Math.floor(containerWidth * cfg.maxContainerFraction) : cfg.ceilingPx;

	const effectiveCeiling = Math.min(cfg.ceilingPx, containerCeiling);
	const effectiveFloor = Math.min(cfg.floorPx, effectiveCeiling);

	const contentBased = Math.max(idealWidth, cfg.minContentPx);
	const width = Math.round(Math.min(Math.max(contentBased, effectiveFloor), effectiveCeiling));

	const willTruncate = rawIdealWidth > width;

	return { width, willTruncate, idealWidth };
}

export function useYAxisWidth(
	attributes: string[],
	containerWidth: number,
	opts?: {
		scaleFactor?: number;
		isMultiTile?: boolean;
		config?: Partial<YAxisWidthConfig>;
	},
): YAxisWidthResult {
	const { scaleFactor = 1, isMultiTile = false, config } = opts ?? {};

	return useMemo(
		() =>
			computeYAxisWidth({
				attributes,
				containerWidth,
				scaleFactor,
				isMultiTile,
				config,
			}),
		[attributes.join("|"), containerWidth, scaleFactor, isMultiTile],
	);
}

/* -------------------- N-Size Status Helpers -------------------- */

export const LOW_N_THRESHOLD = 5;

export enum NSizeLabelText {
	NoData = "No data",
	LowN = "Low n",
}

/** Short, red placeholder rendered inside mini charts when a value is suppressed
 * (n=0 or n<=5). The full "No data" / "Low N" wording remains in tooltips,
 * column headers (NSizeLabel) and the tabular view. */
export const SUPPRESSED_VALUE_PLACEHOLDER = "-";
export const SUPPRESSED_VALUE_COLOR = "#DC2626";

export type NSizeStatus = "no-data" | "low-n" | "normal";

const isNoDataString = (v: unknown): boolean =>
	typeof v === "string" && (v.toLowerCase() === "no data");

/** Coerce a single n_size bound (number | "no data" | null) to a number, treating sentinels as 0. */
const coerceNSizeValue = (v: unknown): number => {
	if (v == null || isNoDataString(v)) return 0;
	const n = typeof v === "number" ? v : Number(v);
	return Number.isFinite(n) ? n : 0;
};

export const getNSizeStatus = (n_size: unknown): NSizeStatus => {
	if (n_size === undefined || n_size === null || isNoDataString(n_size)) return "no-data";
	// Range form { min, max } — used by stacked charts where a series may inherit the
	// column-level n_size range. Classify on the upper bound so a column is only
	// suppressed when even its largest n is below threshold.
	if (typeof n_size === "object") {
		const { min, max } = n_size as { min?: unknown; max?: unknown };
		return getNSizeStatus(Math.max(coerceNSizeValue(min), coerceNSizeValue(max)));
	}
	if (typeof n_size === "number") {
		if (!isFinite(n_size) || n_size === 0) return "no-data";
		if (n_size < LOW_N_THRESHOLD) return "low-n";
	}
	return "normal";
};

/**
 * Returns the label to render for a chart data point given its n_size:
 *  - NSizeLabelText.NoData when n_size is 0 / null / undefined / "No Data"
 *  - NSizeLabelText.LowN   when 0 < n_size <= 5
 *  - null                  otherwise (caller should render the actual value)
 */
export const getNSizeStatusLabel = (n_size: unknown): NSizeLabelText | null => {
	const status = getNSizeStatus(n_size);
	if (status === "no-data") return NSizeLabelText.NoData;
	if (status === "low-n") return NSizeLabelText.LowN;
	return null;
};

export function isLightColor(bgColor: string) {
	// Convert hex to RGB and calculate luminance
	const rgb = parseInt(bgColor.replace("#", ""), 16);
	const r = (rgb >> 16) & 255;
	const g = (rgb >> 8) & 255;
	const b = rgb & 255;
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	return luminance > 0.8;
}
